import { useEffect, useRef, useState } from "react";
import type { Product } from "../api/types";
import { Tape } from "../components/Bits";

/** The tailor's tape box from the demo. While `done` is false it counts up and ticks items off;
 *  when done it shows what was actually read (the product's size_line and source_note). */
export function MeasureBox(props: {
  cand: Product;
  refBrand: string;
  refTag: string;
  fitPoints: number;
  sizes: number;
  personalizedFrom?: number;
  done: boolean;
}) {
  const { cand, refBrand, refTag, fitPoints, sizes, personalizedFrom, done } = props;
  const target = Math.max(1, fitPoints * Math.max(1, sizes));
  const ticks = [
    done ? cand.size_line || `Read the ${cand.brand} page` : `Reading the ${cand.brand} page: sizes, fabric and measurements`,
    `${done ? "Measured" : "Measuring"} your ${refBrand} in size ${refTag}`,
    done ? `Compared ${fitPoints} fit spots across ${sizes} sizes` : `Comparing ${fitPoints} fit spots across every size`,
  ];
  if (personalizedFrom) ticks.push(`${done ? "Applied" : "Applying"} what you told us before`);

  const [count, setCount] = useState(done ? target : 0);
  const [on, setOn] = useState(done ? ticks.length : 0);
  const raf = useRef(0);
  useEffect(() => {
    if (done) {
      setCount(target);
      setOn(99);
      return;
    }
    const t0 = performance.now();
    const step = (t: number) => {
      let k = Math.min(1, (t - t0) / 2100);
      k = 1 - Math.pow(1 - k, 3);
      setCount(Math.round(target * k));
      if (k < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    const timers = [0, 1, 2, 3].map((i) => window.setTimeout(() => setOn((n) => Math.max(n, i + 1)), 250 + i * 480));
    return () => {
      cancelAnimationFrame(raf.current);
      timers.forEach(clearTimeout);
    };
  }, [done, target]);

  return (
    <div className={"measure-box" + (done ? " done" : "")} id="measure" aria-busy={!done}>
      <div className="counter">
        <span className="big">{count}</span>
        <span className="eyebrow">fit spots compared</span>
      </div>
      <Tape fill={done ? 100 : 0} mode={done ? undefined : "measuring"} />
      <ul className="ticks">
        {ticks.map((t, i) => (
          <li key={i} className={i < on ? "on" : undefined}>
            {t}
          </li>
        ))}
      </ul>
      {done && cand.source_note && <p className="foot">{cand.source_note}</p>}
    </div>
  );
}

/** Shown under a hang tag while the server is still reading the product page. */
export function ReadingBox({ p }: { p: Product }) {
  return (
    <div className="measure-box reading" role="status">
      <span className="eyebrow">Reading the {p.brand} page</span>
      <Tape fill={0} mode="waiting" />
      <p className="small muted">Finding the sizes, the fabric and the measurements. This takes a few seconds.</p>
    </div>
  );
}
